import { useCallback, useEffect, useMemo, useState } from 'react'

import { FormHandles } from '@unform/core'
import { AccessibleIcon, IconInfoFill } from '@vtex/shoreline'

import KeywordListInput from '~/components/KeywordListInput'
import { KeywordListTotal } from '~/components/KeywordListTotal'
import Panel from '~/components/Panel'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import * as S from './styles'

/**
 * Componente para lidar com palavras-chave adicional / negativa
 * additional and negative keywords
 */

interface CampaignKeywordProps {
  formRef: FormHandles
}

const TOTAL_KEYWORDS_LIMIT = 100

const CampaignKeywords = ({ formRef }: CampaignKeywordProps) => {
  const { t, tCapitalize: tc } = useAPPTranslation()
  const [additional, setAdditional] = useState<string[]>([])
  const [negative, setNegative] = useState<string[]>([])
  const [negativePendingValues, setNegativePendingValues] = useState<string[]>(
    []
  )
  const [additionalPendingValues, setAdditionalPendingValues] = useState<
    string[]
  >([])

  const handleChangeAdditionalKeywordsList = useCallback(
    ({ value }: { value: string[] }) => {
      setAdditional(value)
    },
    []
  )

  const handleChangeNegativeKeywordsList = useCallback(
    ({ value }: { value: string[] }) => {
      setNegative(value)
    },
    []
  )

  const handleAdditionalPendingValues = useCallback(
    ({ value }: { value: string[] }) => {
      setAdditionalPendingValues(
        value?.filter(item => {
          return !additional?.includes(item)
        })
      )
    },
    [additional]
  )
  const handleNegativePendingValues = useCallback(
    ({ value }: { value: string[] }) => {
      setNegativePendingValues(
        value?.filter(item => {
          return !negative?.includes(item)
        })
      )
    },
    [negative]
  )

  const total = useMemo(() => {
    const _total =
      (additional?.length || 0) +
      (negative?.length || 0) +
      (negativePendingValues?.length || 0) +
      (additionalPendingValues?.length || 0)
    return _total
  }, [additional, negative, additionalPendingValues, negativePendingValues])

  const validateBeforeCreate = useCallback(() => {
    const beforeCreateErrors: string[] = []
    if (total > TOTAL_KEYWORDS_LIMIT) {
      beforeCreateErrors.push(t('rm:campaign.keywords.errors.total_limit'))
    }
    return beforeCreateErrors
  }, [total])

  return (
    <>
      <Panel
        title={t('rm:campaign.keywords.title')}
        description={t('rm:campaign.keywords.description')}
        isCollapsible
        initialCollapsibleState={true}
      >
        <KeywordListTotal total={total} limit={TOTAL_KEYWORDS_LIMIT} />

        <div className="mb-5">
          <KeywordListInput
            name="settings.additional_keywords"
            label={t('rm:campaign.keywords.additional.title')}
            helperText={t('rm:campaign.keywords.additional.description')}
            placeholder={t('rm:campaign.keywords.input.placeholder')}
            onChange={handleChangeAdditionalKeywordsList}
            onChangePendingValues={handleAdditionalPendingValues}
            validateBeforeCreate={validateBeforeCreate}
          />
        </div>

        <div className="mb-4">
          <KeywordListInput
            name="settings.negative_keywords"
            label={t('rm:campaign.keywords.negative.title')}
            helperText={t('rm:campaign.keywords.negative.description')}
            placeholder={t('rm:campaign.keywords.input.placeholder')}
            onChange={handleChangeNegativeKeywordsList}
            onChangePendingValues={handleNegativePendingValues}
            validateBeforeCreate={validateBeforeCreate}
          />
        </div>
      </Panel>
    </>
  )
}

export default CampaignKeywords
